import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import notifiers from "global/constants/NotificationConstants";
import {
  openErrorNotification,
  isTruthy,
  formatDateInLongDateTimeFormat,
  formateRelativeTimeFormat,
} from "helpers/methods";
import { Box, Grid, Skeleton, Stack, Typography } from "@mui/material";
import { doFetchTicketById } from "screens/Tickets/ViewTicket/ViewTicketService";
import { KeyboardBackspace } from "@mui/icons-material";
import history from "utils/history";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import TicketReporterDetails from "./Components/TicketReporterDetails";
import Activity from "screens/Tickets/ViewTicket/Components/Activity";
import TicketDetails from "screens/Tickets/ViewTicket/Components/TicketDetails";
import viewpaths from "global/constants/ViewPathConstants";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

const ViewTicket = () => {
  const { id } = useParams<{ id: string }>();
  const urlParams = new URLSearchParams(useLocation().search);
  const tabValueName = urlParams.get("tab");
  const [isLoading, setIsLoading] = useState(false);
  const [ticket, setTicket] = useState<any>(null);

  useEffect(() => {
    fetchTicketById();
  }, []);

  const fetchTicketById = async () => {
    try {
      setIsLoading(true);
      const response = await doFetchTicketById(id);
      setTicket(response);
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    }
  };

  const getHeader = () => {
    return (
      <Stack direction={"column"} spacing={2}>
        <Stack
          direction={"row"}
          spacing={0.5}
          sx={{ cursor: "pointer" }}
          alignItems={"center"}
          onClick={() => {
            history.push({
              pathname: viewpaths.ticketsViewPath,
              state: {
                fromPageTableValue: tabValueName,
                loadingState: true,
              },
            });
          }}
        >
          <KeyboardBackspace htmlColor="#475569" sx={{ cursor: "pointer" }} />
          <Typography variant="subtitle1" color="text.primary" fontWeight={700}>
            Back
          </Typography>
        </Stack>
        <Stack direction={"column"} spacing={0}>
          {isLoading ? (
            <Skeleton animation="wave" variant="text" height={25} width={300} />
          ) : (
            <Typography variant="h4" color="#7551D0" gutterBottom>
              [#{ticket?.id}] {ticket?.name}
            </Typography>
          )}
          {isLoading ? (
            <Skeleton animation="wave" variant="text" height={25} width={100} />
          ) : (
            <Stack direction="row" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {ticket?.activationDate
                  ? `Created ${formatDateInLongDateTimeFormat(
                      ticket.activationDate
                    )}`
                  : ""}
              </Typography>
              <FiberManualRecordIcon
                fontSize="small"
                sx={{ width: "8px", color: "#47556980" }}
              />
              <Typography variant="body2" color="text.secondary">
                {ticket?.updatedOn
                  ? `Updated ${formateRelativeTimeFormat(ticket.updatedOn)}`
                  : ""}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  };

  const getBody = () => {
    return (
      <Grid container display="flex" spacing={2} mt={1}>
        <Grid item xs={12} sm={12} md={8} lg={8} xl={7}>
          <Grid container display="flex" flexDirection="column" spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <TicketReporterDetails
                ticketDetails={ticket}
                isLoading={isLoading}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <Activity
                ticketDetails={ticket}
                isLoading={isLoading}
                setIsLoading={setIsLoading}
                ticketId={id}
                fetchTicketById={fetchTicketById}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
          <Grid container display="flex" flexDirection="column" spacing={2}>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <TicketDetails ticketDetails={ticket} isLoading={isLoading} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      <CenteredAuthenticatedLayout>
        {getHeader()}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            mt: 2,
            pb: 6,
          }}
        >
          {getBody()}
        </Box>
      </CenteredAuthenticatedLayout>

      <CustomLoader isLoading={isLoading} />
    </>
  );
};

export default ViewTicket;
